import { useState, useEffect, useCallback, PropsWithChildren } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import { sendFEEvent } from '../eventUtil';
import { useAPI } from '../services/api.service';
import { updateAxiosSessionToken } from '../apiUtil';
import {
  Offer,
  ESdkEvents,
  EventParams,
  MixpanelEventParams,
  SubscriptionsSummary,
  SolutionModel,
  SolutionModelSubtype,
} from '../contracts';
import { NuveiSDK } from '../components/Checkout/components/NuveiPayment/types';
import { usePaymentState } from '../components/Checkout/hooks/use-payment-state';
import { CheckoutSummaryContext } from './CheckoutSummaryProvider';
import { buildMixpanelEventParams, ICheckoutSummaryContext } from './summary-provider-utils';
import { getQueryParam } from '../utils/get-query-param';
import { useAppConfig } from '../hooks/use-app-config';
import {
  createActiveSubscriptionError,
  isDuplicateSubscriptionError,
} from '../utils/subscription-errors';

export function SubscriptionContextBridge({ children }: PropsWithChildren) {
  const { showBoundary } = useErrorBoundary();
  const { isBootInitialized, publisherId } = useAppConfig();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionsSummary>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();
  const { sessionToken } = useParams<{ sessionToken: string }>();
  const setPriceBreakdown = usePaymentState((state) => state.setPriceBreakdown);
  const setZipCode = usePaymentState((state) => state.setZipCode);
  const setFullAddress = usePaymentState((state) => state.setFullAddress);
  const promoCodeMetadata = usePaymentState((state) => state.promoCodeMetadata);
  const setCustomerGeoState = usePaymentState((state) => state.setCustomerGeoState);
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  // logic for payment links
  const [redirectUrl, setRedirectUrl] = useState(() => {
    const redirectUrl = getQueryParam('redirectUrl');
    return redirectUrl || '';
  });
  const disableRedirect = getQueryParam('redirect') === 'false';

  const fetchSubscriptionData = useCallback(
    async (sessionToken: string) => {
      try {
        const summaryResponse = await API.getSubscriptionsCheckoutSummary();

        setSubscriptionData(summaryResponse as any);
        setPriceBreakdown(
          summaryResponse.firstPaymentDiscount?.priceBreakdown ||
            summaryResponse.pricing.priceBreakdown,
        );
        setZipCode(summaryResponse.customer.address?.zipCode || '');
        setFullAddress(summaryResponse.customer.address?.address || '');
        setCustomerGeoState(summaryResponse.customer.address?.state);
        if (summaryResponse.redirectUrl) {
          setRedirectUrl(summaryResponse.redirectUrl);
          localStorage.setItem('redirectUrl', summaryResponse.redirectUrl);
        }
      } catch (error: any) {
        if (error?.response?.data?.redirectUrl && !disableRedirect) {
          localStorage.setItem('redirectUrl', error.response.data.redirectUrl);
        }
        const timestamp = new Date().toString();
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          date: timestamp,
          sessionId: sessionToken,
          error: {
            isFatal: true,
          },
        });
        console.error('Error fetching subscription data:', error);
        showBoundary(
          isDuplicateSubscriptionError(error)
            ? createActiveSubscriptionError(t('activeSubscriptionAlreadyAvailable'))
            : t('unableToOpenCheckout'),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      API.getSubscriptionsCheckoutSummary,
      setPriceBreakdown,
      setZipCode,
      setFullAddress,
      setCustomerGeoState,
      showBoundary,
      t,
    ],
  );

  const getEventParams = useCallback(() => {
    const timestamp = Date.now();
    return {
      date: timestamp.toString(),
      sessionId: sessionToken,
      totalSum: subscriptionData?.pricing?.priceBreakdown?.priceTotal,
      totalSumCurrency: subscriptionData?.pricing?.currencyCode,
      userId: subscriptionData?.customer?.id,
      userCountry: subscriptionData?.customer?.geoLocator?.countryCode2,
    } as EventParams;
  }, [subscriptionData, sessionToken, promoCodeMetadata]);

  const enrichEventParams = useCallback(() => {
    return {
      ...getEventParams(),
      publisherId,
    } as EventParams;
  }, [getEventParams, publisherId]);

  const getMixpanelEventParams = useCallback(
    (): MixpanelEventParams => buildMixpanelEventParams(getEventParams(), publisherId),
    [getEventParams, publisherId],
  );

  useEffect(() => {
    // Gate API calls on successful boot initialization
    if (sessionToken && isBootInitialized) {
      updateAxiosSessionToken(sessionToken).then(() => {
        fetchSubscriptionData(sessionToken);
      });
    }
  }, [isBootInitialized, fetchSubscriptionData, location, sessionToken]);

  useEffect(() => {
    if (subscriptionData) {
      sendFEEvent(ESdkEvents.ORDER_CREATED, getEventParams());
    }
  }, [subscriptionData, getEventParams]);

  const contextValue: ICheckoutSummaryContext = {
    checkoutData: subscriptionsDataToCheckoutDataAdapt(subscriptionData),
    subscriptionData: {
      plan: subscriptionData?.plan,
      paymentIntent: subscriptionData?.paymentIntent,
      firstPaymentDiscount: subscriptionData?.firstPaymentDiscount,
      pricing: subscriptionData?.pricing,
    },
    getEventParams,
    getMixpanelEventParams,
    enrichEventParams,
    onPaymentMethodChange: () => {},
    isCheckoutDataLoading: isLoading,
    safeCharge: null as unknown as NuveiSDK,
    selectedPaymentMethodDisplayNameRef: null as any,
    redirectUrl,
    disableRedirect,
  };

  return (
    <CheckoutSummaryContext.Provider value={contextValue}>
      {children}
    </CheckoutSummaryContext.Provider>
  );
}

function subscriptionsDataToCheckoutDataAdapt(
  subscriptionsData: SubscriptionsSummary | undefined,
): Offer | undefined {
  if (subscriptionsData) {
    return {
      offer: {
        name: '',
        sku: '',
        assetUrl: '',
        description: subscriptionsData.plan.description,
      },
      items: [],
      pricing: subscriptionsData.pricing,
      paymentMethods: [],
      pks: subscriptionsData.pks,
      customer: subscriptionsData.customer,
      orderId: '',
      savedCards: [],
      checkoutConfigurations: {
        footerConfiguration: {
          footerText: '',
          defaultText: '',
          defaultCAText: '',
          fullFooterConfig: {},
        },
        showSaveMyCard: false,
        showLinkSavedCard: false,
        //hardcoded for now, will be updated will be updated when working on subscriptions for payment links
        solutionModel: SolutionModel.INDEPENDENT_CHECKOUT,
        solutionModelSubtype: SolutionModelSubtype.WEB_CHECKOUT,
        shouldShowPayPalAsApm: false,
        shouldShowGooglePayAsApm: false,
        featureFlags: subscriptionsData?.checkoutConfigurations?.featureFlags || {},
      },
    } satisfies Offer;
  }
}
